/**
 * Types for the Message Creation system
 */

export type SenderRole = "customer" | "guide" | "builder" | "mentor" | "steward";
export type ReceiverRole = "customer" | "guide" | "builder" | "mentor" | "steward" | "admin";
export type MembershipState = "genes_only" | "dxn_new" | "dxn_active" | "dxn_dormant";
export type MessageStatus = "draft" | "active" | "archived";

export type PrimarySubject =
  | "orientation"
  | "wellness_product"
  | "dxn_understanding"
  | "process_guidance"
  | "ethics_boundaries"
  | "encouragement"
  | "follow_up"
  | "support_help";

export type SecondaryTag = "informational" | "optional" | "time_sensitive" | "clarification" | "reminder";

export type TriggerType = "event" | "action" | "time";

export type EventTrigger =
  | "orientation_completed"
  | "new_dxn_member_marked"
  | "prospect_added"
  | "prospect_converted"
  | "first_order_completed"
  | "inactivity_flag";

export type ActionTrigger =
  | "user_clicks_follow_up"
  | "user_opens_help_needed"
  | "user_views_dxn_new"
  | "user_selects_person_card"
  | "user_opens_downline_view";

export type ToneType = "neutral" | "supportive" | "informative" | "cautionary";

export type SendDirection = "upline_to_downline" | "downline_to_upline";

export type FrequencyLimit = "once_per_event" | "once_per_person" | "unlimited";

export type VisibilityType = "manual_send_only" | "suggested";

export interface MessageTemplate {
  id?: number;
  // Basic Metadata
  title: string;
  status: MessageStatus;
  version_notes?: string;

  // Role & Membership Targeting
  sender_role: SenderRole;
  receiver_role: ReceiverRole;
  membership_states: MembershipState[];

  // Message Category
  primary_subject: PrimarySubject;
  secondary_tags: SecondaryTag[];

  // Trigger Definition
  trigger_type: TriggerType;
  event_trigger?: EventTrigger;
  action_trigger?: ActionTrigger;
  time_trigger_days?: number;

  // Message Content
  message_body: string;
  tone: ToneType;

  // Delivery Rules
  send_direction: SendDirection;
  frequency_limit: FrequencyLimit;
  visibility: VisibilityType;

  // Timestamps
  created_at?: string;
  updated_at?: string;
}

// Display labels for UI
export const SENDER_ROLE_LABELS: Record<SenderRole, string> = {
  customer: "Customer",
  guide: "Guide",
  builder: "Builder",
  mentor: "Mentor",
  steward: "Steward",
};

export const RECEIVER_ROLE_LABELS: Record<ReceiverRole, string> = {
  customer: "Customer",
  guide: "Guide",
  builder: "Builder",
  mentor: "Mentor",
  steward: "Steward",
  admin: "Admin",
};

export const MEMBERSHIP_STATE_LABELS: Record<MembershipState, string> = {
  genes_only: "GENES only",
  dxn_new: "DXN Member – New",
  dxn_active: "DXN Member – Active",
  dxn_dormant: "DXN Member – Dormant",
};

export const PRIMARY_SUBJECT_LABELS: Record<PrimarySubject, string> = {
  orientation: "Orientation",
  wellness_product: "Wellness / Product",
  dxn_understanding: "DXN Understanding",
  process_guidance: "Process Guidance",
  ethics_boundaries: "Ethics / Boundaries",
  encouragement: "Encouragement",
  follow_up: "Follow-up",
  support_help: "Support / Help",
};

export const SECONDARY_TAG_LABELS: Record<SecondaryTag, string> = {
  informational: "Informational",
  optional: "Optional",
  time_sensitive: "Time-sensitive",
  clarification: "Clarification",
  reminder: "Reminder",
};

export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  event: "Event-based",
  action: "Action-based",
  time: "Time-based",
};

export const EVENT_TRIGGER_LABELS: Record<EventTrigger, string> = {
  orientation_completed: "Orientation completed",
  new_dxn_member_marked: "New DXN member marked",
  prospect_added: "Prospect added",
  prospect_converted: "Prospect converted",
  first_order_completed: "First order completed",
  inactivity_flag: "Inactivity (manual flag)",
};

export const ACTION_TRIGGER_LABELS: Record<ActionTrigger, string> = {
  user_clicks_follow_up: "User clicks 'Follow Up'",
  user_opens_help_needed: "User opens 'Help Needed'",
  user_views_dxn_new: "User views 'DXN New'",
  user_selects_person_card: "User selects a person card",
  user_opens_downline_view: "User opens Downline View",
};

export const TONE_LABELS: Record<ToneType, string> = {
  neutral: "Neutral",
  supportive: "Supportive",
  informative: "Informative",
  cautionary: "Cautionary",
};

export const SEND_DIRECTION_LABELS: Record<SendDirection, string> = {
  upline_to_downline: "Upline → Downline",
  downline_to_upline: "Downline → Upline",
};

export const FREQUENCY_LIMIT_LABELS: Record<FrequencyLimit, string> = {
  once_per_event: "Once per event",
  once_per_person: "Once per person",
  unlimited: "Unlimited (admin-only)",
};

export const VISIBILITY_LABELS: Record<VisibilityType, string> = {
  manual_send_only: "Manual send only",
  suggested: "Suggested (visible but optional)",
};

// Placeholder tokens for message body
export const MESSAGE_PLACEHOLDERS = [
  { token: "{{Name}}", description: "Recipient's name" },
  { token: "{{Role}}", description: "Recipient's role" },
  { token: "{{Action}}", description: "Triggering action" },
];
